import React, { useEffect, useState, useContext } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { AuthContext } from '../App';
import usePermissions from '../hooks/usePermissions';
import Navbar from '../components/Navbar';
import PermissionDeniedCard from '../components/PermissionDeniedCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Video, Calendar, Clock, Euro, Users, Edit, UserPlus, List, DollarSign, Gift, Hourglass, Edit2, Trash2, UserCheck, Link as LinkIcon, Copy } from 'lucide-react';

// Form Fields Component (outside main component)
const FormFields = ({ formData, setFormData, groups = [], isEdit = false }) => (
  <>
    <div>
      <Label htmlFor="title">Titolo</Label>
      <Input
        id="title"
        value={formData.title}
        onChange={(e) => setFormData({...formData, title: e.target.value})}
      />
    </div>
    
    <div>
      <Label htmlFor="description">Descrizione</Label>
      <Textarea
        id="description"
        rows={4}
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
    </div>

    <div>
      <Label htmlFor="host_name">Nome Ospite</Label>
      <Input
        id="host_name"
        value={formData.host_name}
        onChange={(e) => setFormData({...formData, host_name: e.target.value})}
      />
    </div>

    <div className="flex items-center space-x-2">
      <Checkbox
        id="is_online"
        checked={formData.is_online}
        onCheckedChange={(checked) => setFormData({...formData, is_online: checked})}
      />
      <Label htmlFor="is_online">Evento online</Label>
    </div>

    {formData.is_online ? (
      <div>
        <Label htmlFor="online_meeting_link">Link Google Meet</Label>
        <Input
          id="online_meeting_link"
          type="url"
          placeholder="https://meet.google.com/..."
          value={formData.online_meeting_link}
          onChange={(e) => setFormData({...formData, online_meeting_link: e.target.value})}
        />
      </div>
    ) : (
      <>
        <div>
          <Label htmlFor="location">Luogo</Label>
          <Input
            id="location"
            placeholder="Via Roma 123, Milano"
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="location_lat">Latitudine</Label>
            <Input
              id="location_lat"
              type="number"
              step="0.000001"
              placeholder="45.4642"
              value={formData.location_lat}
              onChange={(e) => setFormData({...formData, location_lat: e.target.value})}
            />
          </div>
          <div>
            <Label htmlFor="location_lon">Longitudine</Label>
            <Input
              id="location_lon"
              type="number"
              step="0.000001"
              placeholder="9.1900"
              value={formData.location_lon}
              onChange={(e) => setFormData({...formData, location_lon: e.target.value})}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="google_maps_link">Link Google Maps</Label>
          <Input
            id="google_maps_link"
            type="url"
            placeholder="https://maps.google.com/?q=..."
            value={formData.google_maps_link}
            onChange={(e) => setFormData({...formData, google_maps_link: e.target.value})}
          />
        </div>
      </>
    )}

    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="start_datetime">Data e ora inizio (obbligatoria)</Label>
        <Input
          id="start_datetime"
          type="datetime-local"
          value={formData.start_datetime}
          required
          onChange={(e) => setFormData({...formData, start_datetime: e.target.value})}
        />
      </div>
      <div>
        <Label htmlFor="duration_minutes">Durata (minuti)</Label>
        <Input
          id="duration_minutes"
          type="number"
          placeholder="120"
          value={formData.duration_minutes}
          onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
        />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="price">Prezzo (€)</Label>
        <Input
          id="price"
          type="number"
          step="0.01"
          value={formData.price}
          onChange={(e) => setFormData({...formData, price: e.target.value})}
        />
      </div>
      <div>
        <Label htmlFor="registration_deadline">Scadenza iscrizioni (obbligatoria)</Label>
        <Input
          id="registration_deadline"
          type="datetime-local"
          value={formData.registration_deadline}
          required
          onChange={(e) => setFormData({...formData, registration_deadline: e.target.value})}
        />
      </div>
      <div>
        <Label htmlFor="cancellation_deadline">Termine annullamento iscrizione</Label>
        <Input
          id="cancellation_deadline"
          type="datetime-local"
          value={formData.cancellation_deadline}
          onChange={(e) => setFormData({...formData, cancellation_deadline: e.target.value})}
        />
        <p className="text-xs text-gray-500 mt-1">
          Dopo questa data gli utenti non potranno più annullare l&apos;iscrizione
        </p>
      </div>
    </div>
    
    <div>
      <Label htmlFor="registration_group">Gruppo per nuovi utenti (link condivisibile)</Label>
      <Select 
        value={formData.registration_group_id || 'none'} 
        onValueChange={(val) => setFormData({...formData, registration_group_id: val === 'none' ? null : val})}
      >
        <SelectTrigger>
          <SelectValue placeholder="Seleziona gruppo (opzionale)..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Nessuno (default: Partecipanti eventi)</SelectItem>
          {groups.map(g => (
            <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-gray-500 mt-1">
        Gli utenti che si registrano tramite link condivisibile verranno aggiunti a questo gruppo
      </p>
    </div>
  </>
);

const UTC_OFFSET_REGEX = /([zZ]|[+-]\d{2}:\d{2}|[+-]\d{4})$/;

const parseBackendUtcDateTime = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const parsedFromNumber = new Date(value);
    return Number.isNaN(parsedFromNumber.getTime()) ? null : parsedFromNumber;
  }

  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(' ', 'T');
  const utcCandidate = UTC_OFFSET_REGEX.test(normalized) ? normalized : `${normalized}Z`;
  const parsed = new Date(utcCandidate);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const toUtcIsoString = (localDateTimeValue) => {
  if (!localDateTimeValue) return null;
  const parsed = new Date(localDateTimeValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const formatUtcDateTimeForInput = (value) => {
  const parsed = parseBackendUtcDateTime(value);
  if (!parsed) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export default function ActivitiesPage() {
  const { user } = useContext(AuthContext);
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canManageAttendance = !permissionsLoading && hasPermission('ACTIVITY_MANAGE_ATTENDANCE');
  const [activities, setActivities] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [registerOthersDialogOpen, setRegisterOthersDialogOpen] = useState(false);
  const [registrationsDialogOpen, setRegistrationsDialogOpen] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [attendanceUsers, setAttendanceUsers] = useState([]);
  const [attendanceOnlyRegistered, setAttendanceOnlyRegistered] = useState(true);
  const [attendanceSearchTerm, setAttendanceSearchTerm] = useState('');
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [attendanceDraft, setAttendanceDraft] = useState({});
  const [attendanceOriginal, setAttendanceOriginal] = useState({});
  const [selectedUserId, setSelectedUserId] = useState('');
  const [registerableUsers, setRegisterableUsers] = useState([]);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [removingRegistration, setRemovingRegistration] = useState(false);
  const [refundingRegistration, setRefundingRegistration] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    host_name: '',
    is_online: false,
    location: '',
    location_lat: '',
    location_lon: '',
    google_maps_link: '',
    online_meeting_link: '',
    start_datetime: '',
    duration_minutes: '',
    price: '0.00',
    registration_deadline: '',
    cancellation_deadline: '',
    registration_group_id: ''
  });

  useEffect(() => {
    if (permissionsLoading) return;
    
    if (hasPermission('ACTIVITY_VIEW')) {
      fetchActivities();
    } else {
      setLoading(false);
    }
    
    if (hasPermission('ACTIVITY_REGISTER_OTHERS')) {
      fetchAllUsers();
    }
    
    if (hasPermission('ACTIVITY_MANAGE')) {
      fetchGroups();
    }
  }, [permissionsLoading, hasPermission])

  const fetchActivities = async () => {
    try {
      const response = await axios.get('/activities');
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities', error);
      toast.error('Errore nel caricamento eventi');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get('/admin/users');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get('/admin/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups', error);
    }
  };

  const fetchRegistrations = async (activityId) => {
    try {
      const response = await axios.get(`/activities/${activityId}/registrations`);
      setRegistrations(response.data);
    } catch (error) {
      console.error('Failed to fetch registrations', error);
      toast.error('Errore nel caricamento iscrizioni');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      host_name: '',
      is_online: false,
      location: '',
      location_lat: '',
      location_lon: '',
      google_maps_link: '',
      online_meeting_link: '',
      start_datetime: '',
      duration_minutes: '',
      price: '0.00',
      registration_deadline: '',
      cancellation_deadline: '',
      registration_group_id: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.start_datetime) {
      toast.error('La data e ora di inizio è obbligatoria');
      return;
    }

    if (!formData.registration_deadline) {
      toast.error('La scadenza iscrizioni è obbligatoria');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        start_datetime: toUtcIsoString(formData.start_datetime),
        registration_deadline: toUtcIsoString(formData.registration_deadline),
        cancellation_deadline: toUtcIsoString(formData.cancellation_deadline),
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        price: parseFloat(formData.price) || 0.00,
        location_lat: formData.location_lat ? parseFloat(formData.location_lat) : null,
        location_lon: formData.location_lon ? parseFloat(formData.location_lon) : null
      };
      await axios.post('/activities', dataToSend);
      toast.success('Evento creato con successo!');
      setDialogOpen(false);
      resetForm();
      fetchActivities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella creazione evento'));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!formData.start_datetime) {
      toast.error('La data e ora di inizio è obbligatoria');
      return;
    }

    if (!formData.registration_deadline) {
      toast.error('La scadenza iscrizioni è obbligatoria');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        start_datetime: toUtcIsoString(formData.start_datetime),
        registration_deadline: toUtcIsoString(formData.registration_deadline),
        cancellation_deadline: toUtcIsoString(formData.cancellation_deadline),
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        price: parseFloat(formData.price) || 0.00,
        location_lat: formData.location_lat ? parseFloat(formData.location_lat) : null,
        location_lon: formData.location_lon ? parseFloat(formData.location_lon) : null
      };
      await axios.put(`/activities/${selectedActivity.id}`, dataToSend);
      toast.success('Evento aggiornato con successo!');
      setEditDialogOpen(false);
      setSelectedActivity(null);
      resetForm();
      fetchActivities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'aggiornamento evento'));
    }
  };

  const handleEdit = (activity) => {
    setSelectedActivity(activity);
    setFormData({
      title: activity.title || '',
      description: activity.description || '',
      host_name: activity.host_name || '',
      is_online: activity.is_online || false,
      location: activity.location || '',
      location_lat: activity.location_lat || '',
      location_lon: activity.location_lon || '',
      google_maps_link: activity.google_maps_link || '',
      online_meeting_link: activity.online_meeting_link || '',
      start_datetime: formatUtcDateTimeForInput(activity.start_datetime),
      duration_minutes: activity.duration_minutes || '',
      price: activity.price || '0.00',
      registration_deadline: formatUtcDateTimeForInput(activity.registration_deadline),
      cancellation_deadline: formatUtcDateTimeForInput(activity.cancellation_deadline),
      registration_group_id: activity.registration_group_id || ''
    });
    setEditDialogOpen(true);
  };

  const handleRegister = async (activityId) => {
    try {
      // Trova l'attività per verificare il prezzo
      const activity = activities.find(a => a.id === activityId);
      
      // Crea la registrazione
      const response = await axios.post(`/activities/${activityId}/register`);
      const registrationId = response.data.registration_id;
      
      // Se il prezzo è > 0, crea sessione Stripe e reindirizza
      if (activity && activity.price > 0) {
        try {
          const checkoutResponse = await axios.post(
            `/payments/create-checkout-session-activity?registration_id=${registrationId}`
          );
          
          // Reindirizza a Stripe Checkout
          window.location.href = checkoutResponse.data.checkout_url;
        } catch (paymentError) {
          toast.error('Errore nella creazione della sessione di pagamento');
          console.error('Payment error:', paymentError);
        }
      } else {
        // Attività gratuita
        toast.success('Iscrizione confermata!');
        fetchActivities();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'iscrizione'));
    }
  };

  const handleUnregister = async (activityId) => {
    try {
      const response = await axios.delete(`/activities/${activityId}/unregister`);
      
      // Mostra messaggio appropriato in base al rimborso
      if (response.data.refund_processed) {
        toast.success(response.data.message || 'Iscrizione cancellata e rimborso richiesto', { duration: 6000 });
      } else {
        toast.success(response.data.message || 'Iscrizione cancellata');
      }
      
      fetchActivities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella cancellazione'));
    }
  };

  const handleRegisterOther = async () => {
    if (!selectedUserId || !selectedActivity) return;

    try {
      await axios.post(`/activities/${selectedActivity.id}/register-user`, null, {
        params: { user_id: selectedUserId }
      });
      toast.success('Utente iscritto con successo!');
      setRegisterOthersDialogOpen(false);
      setSelectedUserId('');
      fetchActivities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'iscrizione'));
    }
  };

  const openRegisterOthersDialog = (activity) => {
    setSelectedActivity(activity);
    setSelectedUserId('');
    
    axios.get(`/activities/${activity.id}/registrations`)
      .then((response) => {
        const registeredUserIds = new Set(
          (response.data || []).map((reg) => String(reg.user_id))
        );
        const availableUsers = allUsers.filter(
          (u) => !registeredUserIds.has(String(u.id))
        );
        setRegisterableUsers(availableUsers);
      })
      .catch((error) => {
        console.error('Failed to fetch registrations for register-others dialog', error);
        setRegisterableUsers(allUsers);
      })
      .finally(() => {
        setRegisterOthersDialogOpen(true);
      });
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo evento?')) return;
    
    try {
      await axios.delete(`/activities/${activityId}`);
      toast.success('Evento eliminato');
      fetchActivities();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleCopyLink = (shareToken) => {
    const link = `${window.location.origin}/evento/${shareToken}`;
    navigator.clipboard.writeText(link).then(() => {
      toast.success('Link copiato negli appunti!');
    }).catch(() => {
      toast.error('Errore nella copia del link');
    });
  };

  const openRegistrationsList = async (activity) => {
    setSelectedActivity(activity);
    await fetchRegistrations(activity.id);
    setRegistrationsDialogOpen(true);
  };

  const openAttendanceDialog = async (activity) => {
    setSelectedActivity(activity);
    setAttendanceOnlyRegistered(false);
    setAttendanceSearchTerm('');
    setAttendanceDialogOpen(true);
    setAttendanceLoading(true);
    try {
      const response = await axios.get(`/activities/${activity.id}/attendance`);
      const users = response.data?.users || [];
      setAttendanceUsers(users);
      const draftMap = {};
      users.forEach((u) => {
        draftMap[String(u.user_id)] = Number(u.presenza || 0);
      });
      setAttendanceDraft(draftMap);
      setAttendanceOriginal(draftMap);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel caricamento presenze'));
      setAttendanceDialogOpen(false);
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleAttendanceToggle = (userId, targetValue) => {
    const key = String(userId);
    const currentValue = Number(attendanceDraft[key] ?? 0);
    const nextValue = currentValue === Number(targetValue) ? 0 : Number(targetValue);
    setAttendanceDraft((prev) => ({ ...prev, [key]: nextValue }));
  };

  const handleSaveAttendance = async () => {
    if (!selectedActivity) return;

    const updates = Object.entries(attendanceDraft)
      .filter(([userId, presenza]) => Number(attendanceOriginal[userId] ?? 0) !== Number(presenza))
      .map(([user_id, presenza]) => ({ user_id, presenza: Number(presenza) }));

    if (updates.length === 0) {
      toast.info('Nessuna modifica da salvare');
      return;
    }

    setAttendanceSaving(true);
    try {
      const response = await axios.put(`/activities/${selectedActivity.id}/attendance`, { updates });
      toast.success(response.data?.message || 'Presenze salvate');
      await fetchActivities();
      await openAttendanceDialog(selectedActivity);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio presenze'));
    } finally {
      setAttendanceSaving(false);
    }
  };

  const isPaidRegistration = (registration) =>
    registration?.payment_status === 'paid' || registration?.payment_status === 'completed';

  const isSelectedActivityPaid = () => parseFloat(selectedActivity?.price || 0) > 0;

  const canRefundRegistration = (registration) => (
    isSelectedActivityPaid() &&
    isPaidRegistration(registration) &&
    !registration?.is_refunded &&
    registration?.has_payment_intent
  );

  const handleRemoveRegistration = async (withRefund = false) => {
    if (!selectedRegistration || !selectedActivity) return;
    
    setRemovingRegistration(true);
    try {
      const params = new URLSearchParams();
      if (withRefund) {
        params.append('issue_refund', 'true');
      }
      
      const url = `/admin/activities/${selectedActivity.id}/registrations/${selectedRegistration.id}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.delete(url);
      
      if (withRefund && response.data.refund_issued) {
        toast.success(`Iscrizione rimossa e rimborso di €${response.data.refund_amount} emesso`);
      } else {
        toast.success(response.data.message || 'Iscrizione rimossa con successo');
      }
      
      setRemoveDialogOpen(false);
      setSelectedRegistration(null);
      
      // Refresh registrations list and activities
      await fetchRegistrations(selectedActivity.id);
      await fetchActivities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella rimozione dell\'iscrizione'));
    } finally {
      setRemovingRegistration(false);
    }
  };

  const openRemoveDialog = (registration) => {
    setSelectedRegistration(registration);
    setRemoveDialogOpen(true);
  };

  const openRefundDialog = (registration) => {
    setSelectedRegistration(registration);
    setRefundDialogOpen(true);
  };

  const handleRefundOnly = async () => {
    if (!selectedRegistration || !selectedActivity) return;
    setRefundingRegistration(true);
    try {
      const response = await axios.post(
        `/admin/activities/${selectedActivity.id}/registrations/${selectedRegistration.id}/refund`
      );
      toast.success(response.data?.message || 'Rimborso emesso con successo');
      setRefundDialogOpen(false);
      setSelectedRegistration(null);
      await fetchRegistrations(selectedActivity.id);
      await fetchActivities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore durante il rimborso'));
    } finally {
      setRefundingRegistration(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = parseBackendUtcDateTime(dateString);
    if (!date) return 'N/A';
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isDeadlinePassed = (deadline) => {
    if (!deadline) return false;
    const parsedDeadline = parseBackendUtcDateTime(deadline);
    if (!parsedDeadline) return false;
    return parsedDeadline < new Date();
  };

  const isCancellationDeadlinePassed = (deadline) => {
    if (!deadline) return false; // Se non c'è deadline, si può sempre cancellare
    const parsedDeadline = parseBackendUtcDateTime(deadline);
    if (!parsedDeadline) return false;
    return parsedDeadline < new Date();
  };

  const isPastActivity = (activity) => {
    if (!activity?.start_datetime) return false;
    const parsedStartDate = parseBackendUtcDateTime(activity.start_datetime);
    if (!parsedStartDate) return false;
    return parsedStartDate <= new Date();
  };

  // Check if user has permission to view activities
  if (!permissionsLoading && !hasPermission('ACTIVITY_VIEW')) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <PermissionDeniedCard message="Non hai i permessi per visualizzare le attività." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="activities-page">
      <Navbar />

      <div className="pt-20 p-6 max-w-7xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-6 w-6" />
              <h1 className="text-3xl font-bold" data-testid="page-title">Eventi e attività</h1>
            </div>
          </div>
          <div>
          {hasPermission('ACTIVITY_MANAGE') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-black text-white hover:bg-gray-800 w-full sm:w-auto" data-testid="create-activity-button">
                  + Nuovo evento
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Crea nuovo evento</DialogTitle>
                  <DialogDescription>Compila i dettagli dell&apos;evento</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <FormFields formData={formData} setFormData={setFormData} groups={groups} />
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Annulla
                    </Button>
                    <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                      Crea Evento
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : activities.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-gray-500">Nessun evento disponibile al momento</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Sezione Attività Future */}
            {(() => {
              const futureActivities = activities.filter((a) => {
                if (!a.start_datetime) return true;
                const startDate = parseBackendUtcDateTime(a.start_datetime);
                if (!startDate) return true;
                return startDate > new Date();
              });
              
              return futureActivities.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b-2 border-blue-500 pb-2">
                    Attività future
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {futureActivities.map((activity) => (
              <Card key={activity.id} className="hover:shadow-lg transition-shadow border-2 border-blue-200">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{activity.title || 'Evento Senza Titolo'}</CardTitle>
                      <CardDescription className="text-sm">
                        Host: <span className="font-semibold">{activity.host_name || 'N/A'}</span>
                      </CardDescription>
                    </div>
                    {activity.is_online ? (
                      <Video className="w-5 h-5 text-blue-600" />
                    ) : (
                      <MapPin className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {activity.description && <p className="text-gray-700 text-sm">{activity.description}</p>}
                  
                  <div className="space-y-2 text-sm">
                    {activity.start_datetime && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span>{formatDateTime(activity.start_datetime)}</span>
                      </div>
                    )}
                    
                    {activity.duration_minutes && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>{activity.duration_minutes} minuti</span>
                      </div>
                    )}

                    {activity.is_online ? (
                      activity.online_meeting_link && (
                        <a
                          href={activity.online_meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-blue-600 hover:underline"
                        >
                          <Video className="w-4 h-4" />
                          Link Meeting
                        </a>
                      )
                    ) : (
                      <>
                        {activity.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <span>{activity.location}</span>
                          </div>
                        )}
                        {activity.google_maps_link && (
                          <a
                            href={activity.google_maps_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm"
                          >
                            Apri in Google Maps
                          </a>
                        )}
                        {activity.location_lat && activity.location_lon && (
                          <div className="mt-2">
                            <iframe
                              width="100%"
                              height="150"
                              frameBorder="0"
                              title="event-map"
                              src={`https://www.openstreetmap.org/export/embed.html?bbox=${activity.location_lon-0.01},${activity.location_lat-0.01},${activity.location_lon+0.01},${activity.location_lat+0.01}&layer=mapnik&marker=${activity.location_lat},${activity.location_lon}`}
                              style={{ border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                          </div>
                        )}
                      </>
                    )}
                    
                    {activity.price > 0 && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-green-600">€{parseFloat(activity.price).toFixed(2)}</span>
                      </div>
                    )}
                    
                    {activity.registered_count !== undefined && (
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        <span>{activity.registered_count} iscritti</span>
                      </div>
                    )}

                    {activity.registration_deadline && (
                      <div className="text-xs text-gray-500">
                        Iscrizioni entro: {formatDateTime(activity.registration_deadline)}
                      </div>
                    )}
                    
                    {activity.cancellation_deadline && activity.user_registered && (
                      <div className="text-xs text-orange-600 font-medium">
                        Annullamento entro: {formatDateTime(activity.cancellation_deadline)}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 pt-4 border-t">
                    {hasPermission('ACTIVITY_REGISTER_SELF') && (
                      <>
                        {activity.user_registered ? (
                          Number(activity.user_attendance_status || 0) === 1 ? (
                            <Button 
                              className="flex-1 bg-green-600 text-white hover:bg-green-700 disabled:opacity-100"
                              disabled
                              title="Non è possibile annullare dopo registrazione presenza"
                            >
                              PRESENTE
                            </Button>
                          ) : Number(activity.user_attendance_status || 0) === 2 ? (
                            <Button 
                              className="flex-1 bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-100"
                              disabled
                            >
                              ASSENTE
                            </Button>
                          ) : isCancellationDeadlinePassed(activity.cancellation_deadline) ? (
                            <Button 
                              variant="outline"
                              className="flex-1"
                              disabled
                            >
                              Annullamento Non Disponibile
                            </Button>
                          ) : (
                            <Button 
                              variant="outline"
                              className="flex-1"
                              onClick={() => handleUnregister(activity.id)}
                            >
                              Annulla iscrizione
                            </Button>
                          )
                        ) : activity.user_has_pending_payment ? (
                          <Button 
                            className="flex-1 bg-yellow-600 text-white hover:bg-yellow-700"
                            onClick={() => handleRegister(activity.id)}
                          >
                            ⏳ Completa pagamento
                          </Button>
                        ) : isDeadlinePassed(activity.registration_deadline) ? (
                          <Button 
                            variant="outline"
                            className="flex-1"
                            disabled
                          >
                            Iscrizioni chiuse
                          </Button>
                        ) : (
                          <Button 
                            className="flex-1 bg-black text-white hover:bg-gray-800"
                            onClick={() => handleRegister(activity.id)}
                          >
                            Iscriviti
                          </Button>
                        )}
                      </>
                    )}

                    {hasPermission('ACTIVITY_REGISTER_OTHERS') && !isDeadlinePassed(activity.registration_deadline) && (
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={() => openRegisterOthersDialog(activity)}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Iscrivi Membro
                      </Button>
                    )}

                    {hasPermission('ACTIVITY_VIEW_REGISTRATIONS') && (
                      <Button 
                        variant="outline"
                        onClick={() => openRegistrationsList(activity)}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Visualizza iscritti
                      </Button>
                    )}

                    {canManageAttendance && (
                      <Button
                        variant="outline"
                        onClick={() => openAttendanceDialog(activity)}
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Presenze
                      </Button>
                    )}

                    {hasPermission('ACTIVITY_MANAGE') && (
                      <div className="flex gap-2 ml-auto">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyLink(activity.share_token)}
                          title="Copia link di registrazione"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(activity)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(activity.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Sezione Attività Passate */}
            {(() => {
              const pastActivities = activities.filter((a) => {
                if (!a.start_datetime) return false;
                const startDate = parseBackendUtcDateTime(a.start_datetime);
                if (!startDate) return false;
                return startDate <= new Date();
              });
              
              return pastActivities.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold mb-6 text-gray-500 border-b-2 border-gray-400 pb-2">
                    Attività passate
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {pastActivities.map((activity) => (
              <Card key={activity.id} className="hover:shadow-lg transition-shadow bg-gray-50 opacity-80">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-xl">{activity.title || 'Evento Senza Titolo'}</CardTitle>
                        <span className="text-xs bg-gray-600 text-white px-2 py-1 rounded-full">Passata</span>
                      </div>
                      <CardDescription className="text-sm">
                        Host: <span className="font-semibold">{activity.host_name || 'N/A'}</span>
                      </CardDescription>
                    </div>
                    {activity.is_online ? (
                      <Video className="w-5 h-5 text-blue-600" />
                    ) : (
                      <MapPin className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {activity.description && <p className="text-gray-700 text-sm">{activity.description}</p>}
                  
                  <div className="space-y-2 text-sm">
                    {activity.start_datetime && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span>{formatDateTime(activity.start_datetime)}</span>
                      </div>
                    )}
                    
                    {activity.duration_minutes && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>{activity.duration_minutes} minuti</span>
                      </div>
                    )}
                    
                    {activity.is_online ? (
                      activity.online_meeting_link && (
                        <a 
                          href={activity.online_meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-blue-600 hover:underline"
                        >
                          <Video className="w-4 h-4" />
                          Link Meeting
                        </a>
                      )
                    ) : (
                      <>
                        {activity.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <span>{activity.location}</span>
                          </div>
                        )}
                        {activity.google_maps_link && (
                          <a 
                            href={activity.google_maps_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm"
                          >
                            Apri in Google Maps
                          </a>
                        )}
                        {activity.location_lat && activity.location_lon && (
                          <div className="mt-2">
                            <iframe
                              width="100%"
                              height="150"
                              frameBorder="0"
                              title="event-map"
                              src={`https://www.openstreetmap.org/export/embed.html?bbox=${activity.location_lon-0.01},${activity.location_lat-0.01},${activity.location_lon+0.01},${activity.location_lat+0.01}&layer=mapnik&marker=${activity.location_lat},${activity.location_lon}`}
                              style={{ border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                          </div>
                        )}
                      </>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <Euro className="w-4 h-4 text-gray-500" />
                      <span className="font-semibold">
                        {activity.price > 0 ? `€${activity.price}` : 'Gratuito'}
                      </span>
                    </div>
                    
                    {hasPermission('ACTIVITY_VIEW_COUNT') && (
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span>{activity.registered_count} {activity.registered_count === 1 ? 'iscritto' : 'iscritti'}</span>
                      </div>
                    )}
                    
                    {activity.registration_deadline && (
                      <div className="text-xs text-gray-500">
                        Iscrizioni entro: {formatDateTime(activity.registration_deadline)}
                      </div>
                    )}
                    
                    {activity.cancellation_deadline && activity.user_registered && (
                      <div className="text-xs text-orange-600 font-medium">
                        Annullamento entro: {formatDateTime(activity.cancellation_deadline)}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 pt-4 border-t">
                    {hasPermission('ACTIVITY_REGISTER_SELF') && (
                      <>
                        {activity.user_registered ? (
                          Number(activity.user_attendance_status || 0) === 1 ? (
                            <Button 
                              className="flex-1 bg-green-600 text-white hover:bg-green-700 disabled:opacity-100"
                              disabled
                              title="Non è possibile annullare dopo registrazione presenza"
                            >
                              PRESENTE
                            </Button>
                          ) : Number(activity.user_attendance_status || 0) === 2 ? (
                            <Button 
                              className="flex-1 bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-100"
                              disabled
                            >
                              ASSENTE
                            </Button>
                          ) : isCancellationDeadlinePassed(activity.cancellation_deadline) ? (
                            <Button 
                              variant="outline"
                              className="flex-1"
                              disabled
                            >
                              Annullamento Non Disponibile
                            </Button>
                          ) : (
                            <Button 
                              variant="outline"
                              className="flex-1"
                              onClick={() => handleUnregister(activity.id)}
                            >
                              Annulla iscrizione
                            </Button>
                          )
                        ) : activity.user_has_pending_payment ? (
                          <Button 
                            className="flex-1 bg-yellow-600 text-white hover:bg-yellow-700"
                            onClick={() => handleRegister(activity.id)}
                          >
                            ⏳ Completa Pagamento
                          </Button>
                        ) : isPastActivity(activity) ? (
                          <Button
                            className="flex-1 bg-red-600 text-white hover:bg-red-700 disabled:opacity-100"
                            disabled
                          >
                            NON ISCRITTO
                          </Button>
                        ) : isDeadlinePassed(activity.registration_deadline) ? (
                          <Button 
                            variant="outline"
                            className="flex-1"
                            disabled
                          >
                            Iscrizioni chiuse
                          </Button>
                        ) : (
                          <Button 
                            className="flex-1 bg-black text-white hover:bg-gray-800"
                            onClick={() => handleRegister(activity.id)}
                          >
                            Iscriviti
                          </Button>
                        )}
                      </>
                    )}
                    
                    {hasPermission('ACTIVITY_REGISTER_OTHERS') && !isDeadlinePassed(activity.registration_deadline) && (
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setSelectedActivity(activity);
                          setRegisterOthersDialogOpen(true);
                        }}
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {hasPermission('ACTIVITY_VIEW_REGISTRATIONS') && (
                      <Button 
                        variant="outline"
                        onClick={() => openRegistrationsList(activity)}
                      >
                        <List className="w-4 h-4" />
                      </Button>
                    )}

                    {canManageAttendance && (
                      <Button
                        variant="outline"
                        onClick={() => openAttendanceDialog(activity)}
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Presenze
                      </Button>
                    )}
                    
                    {hasPermission('ACTIVITY_MANAGE') && (
                      <>
                        <Button 
                          variant="outline"
                          onClick={() => handleEdit(activity)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="destructive"
                          onClick={() => handleDelete(activity.id)}
                        >
                          Elimina
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifica Evento</DialogTitle>
              <DialogDescription>Aggiorna i dettagli dell&apos;evento</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormFields formData={formData} setFormData={setFormData} groups={groups} isEdit={true} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setEditDialogOpen(false); resetForm(); }}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                  Salva modifiche
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Register Others Dialog */}
        <Dialog open={registerOthersDialogOpen} onOpenChange={setRegisterOthersDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Iscrivi altro utente</DialogTitle>
              <DialogDescription>Seleziona l&apos;utente da iscrivere a {selectedActivity?.title}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="user-select">Utente</Label>
                <select
                  id="user-select"
                  className="w-full p-2 border rounded"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">Seleziona utente...</option>
                  {registerableUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.email})
                    </option>
                  ))}
                </select>
                {registerableUsers.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    Tutti gli utenti risultano già iscritti a questo evento.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setRegisterOthersDialogOpen(false)}>
                  Annulla
                </Button>
                <Button onClick={handleRegisterOther} disabled={!selectedUserId}>
                  Iscrivi
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Attendance Dialog */}
        <Dialog
          open={attendanceDialogOpen}
          onOpenChange={(open) => {
            setAttendanceDialogOpen(open);
            if (!open) {
              setAttendanceUsers([]);
              setAttendanceDraft({});
              setAttendanceOriginal({});
            }
          }}
        >
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Registra presenze - {selectedActivity?.title}</DialogTitle>
              <DialogDescription>
                Imposta la presenza degli utenti. Se segni PRESENTE un non iscritto, verrà creata automaticamente un&apos;iscrizione esentata.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="attendance-only-registered"
                  checked={attendanceOnlyRegistered}
                  onCheckedChange={(checked) => setAttendanceOnlyRegistered(Boolean(checked))}
                />
                <Label htmlFor="attendance-only-registered">Solo iscritti</Label>
                <Input
                  value={attendanceSearchTerm}
                  onChange={(e) => setAttendanceSearchTerm(e.target.value)}
                  placeholder="Cerca per nome..."
                  className="max-w-xs ml-2"
                />
              </div>

              {attendanceLoading ? (
                <div className="text-center py-10 text-gray-500">Caricamento utenti...</div>
              ) : (
                <div className="space-y-2">
                  {(attendanceUsers
                    .filter((u) => !attendanceOnlyRegistered || u.is_registered)
                    .filter((u) => {
                      const term = attendanceSearchTerm.trim().toLowerCase();
                      if (!term) return true;
                      return String(u.user_name || '').toLowerCase().includes(term);
                    })
                    .map((u) => {
                      return (
                        <div key={u.user_id} className="border rounded p-3">
                          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div className="min-w-0">
                              <div className="font-medium">{u.user_name}</div>
                              <div className="text-sm text-gray-600 truncate">{u.user_email}</div>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <span className={`text-xs px-2 py-1 rounded ${u.is_registered ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>
                                  {u.is_registered ? 'ISCRITTO' : 'NON ISCRITTO'}
                                </span>
                                {u.registered_by === 'attendance' && (
                                  <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">
                                    Iscritto da rilevazione presenze
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="w-full md:w-auto">
                              <Label className="text-xs text-gray-500">Stato presenza</Label>
                              <div className="mt-1 flex gap-2">
                                <Button
                                  type="button"
                                  variant={(attendanceDraft[String(u.user_id)] ?? 0) === 1 ? 'default' : 'outline'}
                                  className={(attendanceDraft[String(u.user_id)] ?? 0) === 1 ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
                                  onClick={() => handleAttendanceToggle(u.user_id, 1)}
                                >
                                  PRESENTE
                                </Button>
                                <Button
                                  type="button"
                                  variant={(attendanceDraft[String(u.user_id)] ?? 0) === 2 ? 'default' : 'outline'}
                                  className={(attendanceDraft[String(u.user_id)] ?? 0) === 2 ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                                  onClick={() => handleAttendanceToggle(u.user_id, 2)}
                                >
                                  ASSENTE
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }))}

                  {attendanceUsers
                    .filter((u) => !attendanceOnlyRegistered || u.is_registered)
                    .filter((u) => {
                      const term = attendanceSearchTerm.trim().toLowerCase();
                      if (!term) return true;
                      return String(u.user_name || '').toLowerCase().includes(term);
                    }).length === 0 && (
                    <p className="text-center text-gray-500 py-8">Nessun utente da mostrare</p>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setAttendanceDialogOpen(false)} disabled={attendanceSaving}>
                  Chiudi
                </Button>
                <Button type="button" onClick={handleSaveAttendance} disabled={attendanceLoading || attendanceSaving}>
                  {attendanceSaving ? 'Salvataggio...' : 'Salva presenze'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Registrations List Dialog */}
        <Dialog open={registrationsDialogOpen} onOpenChange={setRegistrationsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Lista Iscritti - {selectedActivity?.title}</DialogTitle>
              <DialogDescription>
                Totale iscritti: {registrations.length}
                {registrations.filter(r => r.is_self_registered).length > 0 && (
                  <span className="ml-2">
                    ({registrations.filter(r => r.is_self_registered).length} autonomi, 
                    {registrations.filter(r => !r.is_self_registered).length} da admin)
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {registrations.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nessun iscritto</p>
              ) : (
                registrations.map((reg) => (
                  <div key={reg.id} className="p-4 border rounded hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{reg.user_name}</span>
                          {reg.is_self_registered ? (
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                              Iscrizione autonoma
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded">
                              Iscritto da: {reg.registered_by_admin || 'Admin'}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">{reg.user_email}</div>
                        {reg.user_phone && (
                          <div className="text-sm text-gray-600">{reg.user_phone}</div>
                        )}
                        <div className="text-xs text-gray-500 mt-2">
                          Iscritto il: {formatDateTime(reg.registered_at)}
                        </div>
                        <div className="flex gap-2 mt-2">
                          {parseFloat(selectedActivity?.price || 0) > 0 && (
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                              (reg.payment_status === 'completed' || reg.payment_status === 'paid')
                                ? 'bg-green-100 text-green-800'
                                : reg.payment_status === 'exempted'
                                  ? 'bg-indigo-100 text-indigo-800'
                                  : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {(reg.payment_status === 'completed' || reg.payment_status === 'paid') ? (
                                <>
                                  <DollarSign className="w-3 h-3" />
                                  PAGATO
                                </>
                              ) : reg.payment_status === 'exempted' ? (
                                <>
                                  <Gift className="w-3 h-3" />
                                  ESENTATO
                                </>
                              ) : (
                                <>
                                  <Hourglass className="w-3 h-3" />
                                  IN ATTESA DI PAGAMENTO
                                </>
                              )}
                            </span>
                          )}
                          {reg.is_refunded && (
                            <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded">
                              Rimborsato: €{reg.refund_amount}
                            </span>
                          )}
                        </div>
                      </div>
                      {hasPermission('ACTIVITY_MANAGE_REGISTRATIONS') && (
                        <div className="ml-4 flex gap-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => openRemoveDialog(reg)}
                          >
                            ELIMINA
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!canRefundRegistration(reg)}
                            onClick={() => openRefundDialog(reg)}
                            title={!canRefundRegistration(reg) ? 'Rimborso disponibile solo con pagamento esistente e non già rimborsato' : 'Emetti rimborso senza eliminare iscrizione'}
                          >
                            RIMBORSA
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Remove Registration Confirmation Dialog */}
        <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Conferma rimozione iscrizione</DialogTitle>
              <DialogDescription>
                Sei sicuro di voler rimuovere l&apos;iscrizione di <strong>{selectedRegistration?.user_name}</strong>?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {isSelectedActivityPaid() ? (
                canRefundRegistration(selectedRegistration) ? (
                  <p className="text-sm text-gray-700 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    Scegli se eliminare con rimborso Stripe oppure senza rimborso.
                  </p>
                ) : (
                  <p className="text-sm text-gray-700 p-3 bg-gray-50 border rounded">
                    Rimborso non disponibile: pagamento assente/non pagato oppure già rimborsato. Puoi solo eliminare senza rimborso.
                  </p>
                )
              ) : (
                <p className="text-sm text-gray-700 p-3 bg-gray-50 border rounded">
                  Evento gratuito: conferma eliminazione iscrizione.
                </p>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setRemoveDialogOpen(false);
                    setSelectedRegistration(null);
                  }}
                  disabled={removingRegistration}
                >
                  Annulla
                </Button>
                {isSelectedActivityPaid() ? (
                  <>
                    <Button 
                      variant="outline"
                      onClick={() => handleRemoveRegistration(false)}
                      disabled={removingRegistration}
                    >
                      {removingRegistration ? 'Eliminazione...' : 'Elimina senza rimborso'}
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => handleRemoveRegistration(true)}
                      disabled={removingRegistration || !canRefundRegistration(selectedRegistration)}
                    >
                      {removingRegistration ? 'Eliminazione...' : 'Elimina con rimborso'}
                    </Button>
                  </>
                ) : (
                  <Button 
                    variant="destructive"
                    onClick={() => handleRemoveRegistration(false)}
                    disabled={removingRegistration}
                  >
                    {removingRegistration ? 'Eliminazione...' : 'Conferma eliminazione'}
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Conferma rimborso</DialogTitle>
              <DialogDescription>
                Vuoi emettere il rimborso per <strong>{selectedRegistration?.user_name}</strong> senza eliminare l&apos;iscrizione?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-700 p-3 bg-yellow-50 border border-yellow-200 rounded">
                Verrà richiesto il rimborso su Stripe e la persona resterà iscritta.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setRefundDialogOpen(false);
                    setSelectedRegistration(null);
                  }}
                  disabled={refundingRegistration}
                >
                  Annulla
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleRefundOnly}
                  disabled={refundingRegistration}
                >
                  {refundingRegistration ? 'Rimborso...' : 'Conferma rimborso'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
